const base = process.env.PUBLIC_URL || "";

export const BOOK_BLOG_POSTS = [
  {
    slug: "sunday-shelf-reset",
    tag: "Vlog",
    title: "Sunday Shelf Reset",
    author: "Mira Holloway",
    date: "Apr 12, 2026",
    readTime: "4 min read",
    excerpt:
      "A quiet morning reorganizing my PagePeers shelf — what stayed, what moved to \"up next,\" and one surprise re-read.",
    cover: `${base}/images/quiet-shelf/stack.jpg`,
    topics: ["Shelf", "Routine", "Vlog"],
  },
  {
    slug: "late-train-book-review",
    tag: "Review",
    title: "Reading on the Late Train",
    author: "PagePeers Editors",
    date: "Apr 5, 2026",
    readTime: "6 min read",
    excerpt:
      "A literary novel that earns its slow burn — notes from a week of platform commutes and one unforgettable final chapter.",
    cover: `${base}/images/reader-circles/vintage.jpg`,
    topics: ["Fiction", "Review", "Commute"],
  },
  {
    slug: "cozy-mystery-haul",
    tag: "Essay",
    title: "The Cozy Mystery Haul",
    author: "Priya Rao",
    date: "Mar 28, 2026",
    readTime: "5 min read",
    excerpt:
      "Why gentle mysteries are having a moment in our circles — and the three titles everyone is saving this month.",
    cover: `${base}/images/rainy-day-reads/figure-1.jpg`,
    topics: ["Cozy", "Mystery", "Trending"],
  },
  {
    slug: "circle-highlights-march",
    tag: "Community",
    title: "Circle Highlights: March",
    author: "PagePeers Club",
    date: "Mar 20, 2026",
    readTime: "5 min read",
    excerpt:
      "Lines our members could not stop quoting — spoiler-safe snippets from five active reader circles.",
    cover: `${base}/images/reader-circles/hero.jpg`,
    topics: ["Circles", "Quotes", "Roundup"],
  },
  {
    slug: "rainy-window-reading-log",
    tag: "Diary",
    title: "Rainy Window Reading Log",
    author: "Arjun Mehra",
    date: "Mar 14, 2026",
    readTime: "4 min read",
    excerpt:
      "Grey skies, one blanket, two finished books — a short diary entry on reading without a productivity goal.",
    cover: `${base}/images/rainy-day-reads/figure-2.jpg`,
    topics: ["Diary", "Cozy", "Slow living"],
  },
  {
    slug: "letting-go-reading-guilt",
    tag: "Essay",
    title: "Letting Go of Reading Guilt",
    author: "Neha Saxena",
    date: "Mar 8, 2026",
    readTime: "7 min read",
    excerpt:
      "DNFs, dusty TBR piles, and the freedom of reading for pleasure again — an essay from the PagePeers journal.",
    cover: `${base}/images/letters-and-tea/hero.jpg`,
    topics: ["Mindset", "Essay", "Habits"],
  },
];

export const BOOK_BLOG_ARTICLES = {
  "sunday-shelf-reset": {
    lede: "Some Sundays are for laundry; this one was for spines.",
    intro:
      "I filmed a short shelf reset for the PagePeers journal — no voiceover, just tea and drag-and-drop. Here is what changed on my shelf and why.",
    sections: [
      {
        heading: "Cleared \"reading now\" down to two",
        body: "I had five books in progress, which meant I was finishing none. I moved three to \"up next\" with a note about why they matter. Suddenly the desk felt honest.",
      },
      {
        heading: "Promoted a forgotten favorite",
        body: "A novel I finished last winter went back to \"favorites\" with a one-line memory: read on the porch, cried at chapter nineteen. Metadata as nostalgia.",
      },
      {
        heading: "Logged a gentle DNF",
        body: "One acclaimed title left the active shelf without shame. I marked it unread-for-now and wrote \"wrong season\" in notes. That counts as progress.",
      },
    ],
    closing:
      "The shelf looks the same to guests. To me, it looks possible again — which is the whole point of a reset.",
  },
  "late-train-book-review": {
    lede: "The best commute books know when to whisper.",
    intro:
      "Our editors read a coastal family saga in platform installments — three stops per chapter, one gasp at Union Station.",
    sections: [
      {
        heading: "Atmosphere over plot machinery",
        body: "The novel trusts weather, meals, and silence. If you need a hook every ten pages, wait for a rainy week. If you love character, board the train.",
      },
      {
        heading: "A narrator worth following",
        body: "Wry, tired, funny without performing — the voice feels like a friend who already knows how the story ends but wants you to enjoy the ride.",
      },
      {
        heading: "Perfect for reader circles",
        body: "Chapter lanes in Late Train Book Club stayed busy all month. Late joiners reported feeling welcome — rare praise for a mid-list literary pick.",
      },
    ],
    closing:
      "Verdict: keep it on your \"up next\" shelf for the next grey commute week. Bring tissues for the last twenty pages.",
  },
  "cozy-mystery-haul": {
    lede: "No gore, plenty of scones.",
    intro:
      "Cozy Mystery Guild on PagePeers voted March's stack — bookshop settings, small-town sleuths, and crimes solved over tea.",
    sections: [
      {
        heading: "Why cozies are surging",
        body: "Readers want stakes without adrenaline overload. Cozies promise justice, warmth, and a return to normal by the final chapter.",
      },
      {
        heading: "Three titles on every save list",
        body: "A seaside inn mystery, a librarian amateur sleuth debut, and a second-chance romance with a missing manuscript — all logged fifty-plus saves this month.",
      },
      {
        heading: "How to start if you are new",
        body: "Join the guild, lurk one weekly highlight thread, and pick whichever cover makes you want to bake something. That is a valid selection method.",
      },
    ],
    closing:
      "Cozy reading is not lesser reading. It is reading that lets you breathe.",
  },
  "circle-highlights-march": {
    lede: "The best lines from the porch light circles.",
    intro:
      "Each month we collect spoiler-safe highlights members agreed to share publicly — one sentence that made someone pause mid-page.",
    sections: [
      {
        heading: "Soft Sci-Fi Salon",
        body: "\"Hope is not optimism — it is maintenance.\" Forty replies, zero fights, three new shelf saves.",
      },
      {
        heading: "Essays at Dusk",
        body: "A member quoted: \"Grief is just love with nowhere to go tonight.\" The thread turned into recommendations for gentle memoir — exactly what essays circles do best.",
      },
      {
        heading: "Weekend Turners",
        body: "Saturday sprint finished in one sitting. Sunday thread: \"I laundry-folded with adrenaline.\" Relatable.",
      },
    ],
    closing:
      "Want your circle featured? Pin a welcome note, keep lanes tidy, and share highlights weekly. We read every nomination.",
  },
  "rainy-window-reading-log": {
    lede: "Two books, zero pages-per-hour math.",
    intro:
      "A diary entry from a rainy Saturday — no review scores, just what it felt like to read without tracking performance.",
    sections: [
      {
        heading: "Morning: essays",
        body: "One essay collection, two chapters, lots of staring at the window between paragraphs. Marked \"reading\" in PagePeers and left it there.",
      },
      {
        heading: "Afternoon: novel",
        body: "Switched to fiction when the rain thickened. Finished before dark. Logged finish time without noting speed — liberation.",
      },
      {
        heading: "Evening: circle check-in",
        body: "Posted one line in Rainy Day Reads chat: \"Today was enough.\" Six heart reactions. No one asked for a rating.",
      },
    ],
    closing:
      "Rain days are proof that reading can be weather, not work.",
  },
  "letting-go-reading-guilt": {
    lede: "You are allowed to read badly, slowly, or not at all.",
    intro:
      "The PagePeers journal exists for honest readers. This essay is for anyone carrying a TBR like a debt statement.",
    sections: [
      {
        heading: "DNFs are data, not failure",
        body: "Stopping a book clarifies taste. PagePeers keeps your history so you can see patterns — not to punish you.",
      },
      {
        heading: "The pile is not a boss",
        body: "Wishlists are menus, not assignments. Move titles in and out of \"up next\" like seasons change.",
      },
      {
        heading: "Community without comparison",
        body: "Circles share enthusiasm, not leaderboards. When guilt creeps in, lurk until joy returns. It usually does.",
      },
    ],
    closing:
      "Reading guilt is loud; pleasure is quiet. Turn toward the quiet.",
  },
};
